/**
 * How far a company challenge has got, and how long is left.
 *
 * Pure calendar and ratio maths, shared because two surfaces state the same
 * two numbers: the web challenge card, and the Slack channel post that
 * mirrors it. A percentage that disagreed between the app and the channel
 * would read as one of them being wrong.
 *
 * Distance, not steps, is the denominator: the target a company signs up to is
 * a route, and the route is measured in metres.
 *
 * **Whose day is it?** A challenge ends on a calendar date, so "how long is
 * left" depends on which day the reader is currently in. A browser can use its
 * own clock, because that clock already is the member's local time. A server
 * cannot: a pod runs in UTC, so between UTC midnight and local midnight it
 * would tell a US company still walking that the challenge had ended. Server
 * callers therefore pass the member's IANA zone, and the day boundary follows
 * it. Omitting the zone keeps the browser's process-local behaviour, which is
 * why the web card needs no changes.
 */
import { daysBetweenKeys } from "./date-key.js";
import { dayKey } from "./interpret/series.js";
/**
 * Whole calendar days from today to the end date. Negative once it has
 * passed, zero on the end date itself.
 *
 * This exists because `remainingChallengeDays` collapses "ends today" and
 * "ended last week" into the same 0, which is fine for a progress bar and
 * wrong for a label: a challenge being walked on its final day must not say
 * it has ended. Callers that need to tell those apart use the sign here.
 */
export function daysUntilCalendarDate(isoDate, now, timeZone) {
    // Both sides are calendar-date keys, so the subtraction is pure day
    // arithmetic with no timezone left in it.
    return daysBetweenKeys(todayKey(now, timeZone), isoDate);
}
/**
 * The reader's current calendar day. With a zone it is that zone's day; without
 * one it is the process-local day, which is the browser's own day.
 */
function todayKey(now, timeZone) {
    if (timeZone)
        return dayKey(now.getTime(), timeZone);
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const day = now.getDate();
    return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}
/**
 * Days the challenge still has, counting today as one of them. Zero on and
 * after the end date, so a caller that renders a countdown stops at nought
 * rather than going negative.
 */
export function remainingChallengeDays(endsOn, now, timeZone) {
    const diff = daysUntilCalendarDate(endsOn, now, timeZone);
    if (diff <= 0)
        return 0;
    return diff + 1;
}
/** A calendar date is a wall-clock day, so it is parsed in local time. */
export function parseCalendarDate(isoDate) {
    const [year, month, day] = isoDate.split("-").map(Number);
    return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1);
}
/** Clamped to 0..1: a company that overshoots its route is at 100%, not 140%. */
export function challengeProgressFraction(progressMeters, targetMeters) {
    if (targetMeters <= 0)
        return 0;
    return Math.min(1, Math.max(0, progressMeters / targetMeters));
}
/**
 * The fraction as a label. A decimal below ten percent, because a long route
 * spends its first week rounding to "0%", which reads as nothing happening.
 */
export function challengeProgressPercent(fraction) {
    const clamped = Math.min(1, Math.max(0, fraction));
    if (clamped === 0)
        return "0%";
    if (clamped === 1)
        return "100%";
    const percent = clamped * 100;
    if (percent < 10)
        return `${percent.toFixed(1)}%`;
    return `${Math.round(percent)}%`;
}
/** "13 days left", "Last day", or an honest past tense once it is over. */
export function challengeRemainingLabel(endsOn, now, timeZone) {
    const until = daysUntilCalendarDate(endsOn, now, timeZone);
    if (until < 0)
        return "Challenge ended";
    if (until === 0)
        return "Last day";
    return `${remainingChallengeDays(endsOn, now, timeZone).toLocaleString("en-US")} days left`;
}
//# sourceMappingURL=challenge-progress.js.map